import type { ReactElement } from 'react';
import type { PrivateDurationTier } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { useModelShowPricing } from './useModelShowPricing.js';
import './ModelShowPricingCard.css';

function parseIntOrZero(value: string): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function ModelDurationTiersCard(): ReactElement {
  const { pricing, setPricing, isLoading, isSaving, error, save } = useModelShowPricing();

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.pricing.loading')}</p>;
  }

  if (!pricing) {
    return <p className="fm-error">{error ?? t('common.studio.pricing.loadError')}</p>;
  }

  const updateTier = (index: number, patch: Partial<PrivateDurationTier>): void => {
    const tiers = pricing.privateDurationTiers.map((tier, tierIndex) =>
      tierIndex === index ? { ...tier, ...patch } : tier,
    );
    setPricing({ ...pricing, privateDurationTiers: tiers });
  };

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0 }}>{t('common.studio.pricing.durationTiersTitle')}</h2>
      <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.pricing.durationTiersHint')}</p>

      <label className="studio-pricing-fields__label">
        {t('common.studio.pricing.untimedDeposit')}
        <input
          type="number"
          min={1}
          max={30}
          value={pricing.untimedDepositMinutes}
          onChange={(event) => {
            setPricing({
              ...pricing,
              untimedDepositMinutes: parseIntOrZero(event.target.value),
            });
          }}
        />
        <span className="studio-pricing-fields__hint">{t('common.studio.pricing.untimedDepositHint')}</span>
      </label>

      <div className="studio-pricing-tiers">
        {pricing.privateDurationTiers.map((tier, index) => (
          <div key={`tier-${String(index)}`} className="studio-pricing-tier">
            <label>
              {t('common.studio.pricing.durationTierMinutes')}
              <input
                type="number"
                min={15}
                max={480}
                value={tier.minutes}
                onChange={(event) => {
                  updateTier(index, { minutes: parseIntOrZero(event.target.value) });
                }}
              />
            </label>
            <label>
              {t('common.studio.pricing.durationTierDiscount')}
              <input
                type="number"
                min={0}
                max={75}
                value={tier.discountPercent}
                onChange={(event) => {
                  updateTier(index, { discountPercent: parseIntOrZero(event.target.value) });
                }}
              />
            </label>
          </div>
        ))}
      </div>

      {error ? <p className="fm-error">{error}</p> : null}
      <div style={{ marginTop: '1rem' }}>
        <Button
          label={isSaving ? t('common.studio.pricing.saving') : t('common.studio.pricing.saveDurationTiers')}
          onClick={() => {
            void save({
              privateDurationTiers: pricing.privateDurationTiers,
              untimedDepositMinutes: pricing.untimedDepositMinutes,
            });
          }}
          disabled={isSaving}
        />
      </div>
    </div>
  );
}
